import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  Chip,
  TablePagination,
  Alert,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  Update,
  MonetizationOn,
  Receipt,
  Person,
  AttachMoney,
  CheckCircle,
  Warning,
  Business,
  AccountBalance,
  TrendingUp,
} from '@mui/icons-material';

const AllowPayoutCommission = () => {
  const canWritePayout = hasWritePermission('plot-booking.allow-payout-commission') || hasWritePermission('booking.allow-payout-commission');
  const [searchForm, setSearchForm] = useState({
    bookingId: '',
  });

  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState('');
  const [isAllowedPayout, setIsAllowedPayout] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [tabValue, setTabValue] = useState(0);

  // Sample booking data for payout commission
  const [bookingData] = useState([
    {
      id: 1,
      bookingId: 'BK001',
      plotNo: 'P101',
      loginId: 'LG001',
      customerName: 'John Smith',
      bookingDate: '2025-01-15',
      status: 'Active',
      siteName: 'Green Valley',
      sectorName: 'Sector A',
      blockName: 'Block 1',
      plotSize: '30x40 ft',
      plotArea: '1200 sq ft',
      mobileNo: '9876543210',
      plotAmount: '₹54,00,000',
      paidAmount: '₹5,40,000',
      balanceAmount: '₹48,60,000',
      payoutCommissionStatus: 'Pending',
      associateName: 'Sarah Wilson',
      associateId: 'ASSOC001',
      commissionAmount: '₹2,70,000',
      commissionPercentage: '5%',
    },
    {
      id: 2,
      bookingId: 'BK002',
      plotNo: 'P205',
      loginId: 'LG002',
      customerName: 'Sarah Johnson',
      bookingDate: '2025-02-20',
      status: 'Active',
      siteName: 'Hill View',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      plotSize: '35x45 ft',
      plotArea: '1575 sq ft',
      mobileNo: '9876543211',
      plotAmount: '₹81,90,000',
      paidAmount: '₹20,47,500',
      balanceAmount: '₹61,42,500',
      payoutCommissionStatus: 'Approved',
      associateName: 'Mike Davis',
      associateId: 'ASSOC002',
      commissionAmount: '₹4,09,500',
      commissionPercentage: '5%',
    },
    {
      id: 3,
      bookingId: 'BK003',
      plotNo: 'P321',
      loginId: 'LG003',
      customerName: 'Michael Brown',
      bookingDate: '2025-03-10',
      status: 'Active',
      siteName: 'Paradise Heights',
      sectorName: 'Sector C',
      blockName: 'Block 3',
      plotSize: '40x50 ft',
      plotArea: '2000 sq ft',
      mobileNo: '9876543212',
      plotAmount: '₹1,20,00,000',
      paidAmount: '₹12,00,000',
      balanceAmount: '₹1,08,00,000',
      payoutCommissionStatus: 'Rejected',
      associateName: 'Lisa Chen',
      associateId: 'ASSOC003',
      commissionAmount: '₹6,00,000',
      commissionPercentage: '5%',
    },
    {
      id: 4,
      bookingId: 'BK004',
      plotNo: 'P456',
      loginId: 'LG004',
      customerName: 'Emily Davis',
      bookingDate: '2025-04-05',
      status: 'Active',
      siteName: 'Royal Gardens',
      sectorName: 'Sector D',
      blockName: 'Block 4',
      plotSize: '45x55 ft',
      plotArea: '2475 sq ft',
      mobileNo: '9876543213',
      plotAmount: '₹1,85,62,500',
      paidAmount: '₹37,12,500',
      balanceAmount: '₹1,48,50,000',
      payoutCommissionStatus: 'Pending',
      associateName: 'Robert Kim',
      associateId: 'ASSOC004',
      commissionAmount: '₹9,28,125',
      commissionPercentage: '5%',
    },
  ]);

  const handleInputChange = (field, value) => {
    setSearchForm({
      ...searchForm,
      [field]: value,
    });
  };

  const handleSearch = async () => {
    if (!searchForm.bookingId.trim()) {
      setErrors({ bookingId: 'Please enter a Booking ID' });
      return;
    }

    setSearchLoading(true);
    setErrors({});

    // Simulate API call
    setTimeout(() => {
      setShowBookingDetails(true);
      setPage(0);
      setSuccessMessage('');
      setIsAllowedPayout(''); // Reset radio selection
      console.log('Searching for booking:', searchForm.bookingId);
      setSearchLoading(false);
    }, 1500);
  };

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleUpdateStatus = async () => {
    if (!isAllowedPayout) {
      setErrors({ payout: 'Please select payout status' });
      return;
    }

    setSubmitLoading(true);

    // Simulate API call
    setTimeout(() => {
      const statusText = isAllowedPayout === 'yes' ? 'approved' : 'rejected';
      setSuccessMessage(`Payout commission has been ${statusText} for Booking ${searchForm.bookingId}`);
      console.log('Payout status updated:', isAllowedPayout);
      setSubmitLoading(false);
    }, 2000);
  };

  // Calculate statistics
  const totalBookings = bookingData.length;
  const pendingPayout = bookingData.filter(booking => booking.payoutCommissionStatus === 'Pending').length;
  const approvedPayout = bookingData.filter(booking => booking.payoutCommissionStatus === 'Approved').length;
  const rejectedPayout = bookingData.filter(booking => booking.payoutCommissionStatus === 'Rejected').length;
  const totalCommission = bookingData.reduce((sum, booking) => {
    const amount = parseFloat(booking.commissionAmount.replace(/[₹,]/g, ''));
    return sum + amount;
  }, 0);

  const getFilteredByStatus = (status) => {
    return getFilteredBookingData().filter(booking => !status || booking.payoutCommissionStatus === status);
  };

  const getFilteredBookingData = () => {
    if (!showBookingDetails || !searchForm.bookingId.trim()) return [];

    return bookingData.filter(booking =>
      booking.bookingId.toLowerCase().includes(searchForm.bookingId.toLowerCase())
    );
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Active':
        return {
          backgroundColor: '#4caf50',
          color: 'white',
        };
      case 'Inactive':
        return {
          backgroundColor: '#f44336',
          color: 'white',
        };
      case 'Hold':
        return {
          backgroundColor: '#ff9800',
          color: 'white',
        };
      default:
        return {
          backgroundColor: '#2196f3',
          color: 'white',
        };
    }
  };

  const getPayoutStatusStyle = (status) => {
    switch (status) {
      case 'Pending':
        return {
          backgroundColor: '#ff9800',
          color: 'white',
        };
      case 'Approved':
        return {
          backgroundColor: '#4caf50',
          color: 'white',
        };
      case 'Rejected':
        return {
          backgroundColor: '#f44336',
          color: 'white',
        };
      default:
        return {};
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Payout Commission Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage and approve commission payouts for associates
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Receipt sx={{ color: '#1976d2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Bookings
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {totalBookings}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#fff3e0', 
            border: '1px solid #ffcc02',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Warning sx={{ color: '#f57c00', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Pending Payout
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {pendingPayout}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <CheckCircle sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Approved Payout
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {approvedPayout}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#ffebee', 
            border: '1px solid #ffcdd2',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <AccountBalance sx={{ color: '#d32f2f', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Rejected Payout
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {rejectedPayout}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <TrendingUp sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Commission
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  ₹{(totalCommission / 100000).toFixed(1)}L
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid> */}

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <MonetizationOn sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search Booking for Payout Commission
            </Typography>
          </Box>

          <Box display="flex" gap={2} alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Booking ID"
                variant="outlined"
                fullWidth
                value={searchForm.bookingId}
                onChange={(e) => handleInputChange('bookingId', e.target.value)}
                placeholder="Enter Booking ID (e.g., BK001)"
                error={!!errors.bookingId}
                helperText={errors.bookingId || "Enter the booking ID to search for payout commission details"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', height: '56px', mt: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={searchLoading || !searchForm.bookingId.trim()}
                startIcon={<Search />}
                sx={{
                  height: '40px',
                  minHeight: '40px',
                  minWidth: '160px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2
                }}
              >
                {searchLoading ? 'Searching...' : 'Search Booking'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      {showBookingDetails && (
        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ px: 3 }}>
              <Tab label={`All Bookings (${getFilteredBookingData().length})`} />
              <Tab label={`Pending Payout (${getFilteredByStatus('Pending').length})`} />
              <Tab label={`Approved Payout (${getFilteredByStatus('Approved').length})`} />
              <Tab label={`Rejected Payout (${getFilteredByStatus('Rejected').length})`} />
            </Tabs>
          </Box>
        </Card>
      )}

      {/* Booking Details Table */}
      {showBookingDetails && (
        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Business sx={{ color: '#1976d2', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Booking Details
                </Typography>
              </Box>

              <Typography variant="body2" color="primary" sx={{ mt: 2, fontWeight: 600 }}>
                Found {getFilteredBookingData().length} booking(s) matching Booking ID: "{searchForm.bookingId}"
              </Typography>
            </Box>

            {getFilteredBookingData().length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Booking Info</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Customer Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Plot Information</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Associate Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Commission Info</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredBookingData()
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((booking, index) => (
                        <TableRow key={booking.id} hover>
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {booking.bookingId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.loginId} • {booking.bookingDate}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {booking.customerName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.mobileNo}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {booking.plotNo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.siteName} • {booking.sectorName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {booking.plotSize} ({booking.plotArea})
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {booking.associateName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.associateId}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                {booking.commissionAmount}
                              </Typography>
                              <Typography variant="caption" color="info.main" display="block">
                                {booking.commissionPercentage}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={booking.payoutCommissionStatus}
                              sx={getPayoutStatusStyle(booking.payoutCommissionStatus)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Alert severity="warning" sx={{ mx: 3, mb: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    No Booking Found
                  </Typography>
                  <Typography variant="body2">
                    No booking found with Booking ID: "{searchForm.bookingId}"
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Please check the booking ID and try again.
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Pagination */}
            <TablePagination
              
              component="div"
              count={getFilteredBookingData().length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ px: 3, py: 2 }}
            />
          </CardContent>
        </Card>
      )}

      {/* Commission Payout Details and Approval */}
      {showBookingDetails && getFilteredBookingData().length > 0 && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={4}>
              <AttachMoney sx={{ color: '#1976d2', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                Commission Payout Details & Approval
              </Typography>
            </Box>

            {/* Commission Information Cards */}
            {getFilteredBookingData().map((booking) => (
              <Card key={booking.id} sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <AttachMoney sx={{ color: '#2e7d32', fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                        Financial Details
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">Plot Amount</Typography>
                    <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600, mb: 1 }}>
                      {booking.plotAmount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Commission Percentage</Typography>
                    <Typography variant="body1" sx={{ color: 'info.main', fontWeight: 600 }}>
                      {booking.commissionPercentage}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <TrendingUp sx={{ color: '#f57c00', fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#f57c00' }}>
                        Commission Amount
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ color: 'warning.main', fontWeight: 700 }}>
                      {booking.commissionAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Person sx={{ color: '#1976d2', fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        Associate Details
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">Associate Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {booking.associateName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Associate ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {booking.associateId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                        Current Status
                      </Typography>
                    </Box>
                    <Chip
                      label={booking.payoutCommissionStatus}
                      sx={getPayoutStatusStyle(booking.payoutCommissionStatus)}
                      size="medium"
                    />
                  </Grid>
                </Grid>
              </Card>
            ))}

            {/* Payout Approval Section */}
            <Card sx={{ p: 3, backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Update sx={{ color: '#1976d2', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Payout Approval Decision
                </Typography>
              </Box>

              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormLabel component="legend" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
                  Is Allowed Payout?
                </FormLabel>
                <RadioGroup
                  row
                  value={isAllowedPayout}
                  onChange={(e) => setIsAllowedPayout(e.target.value)}
                  sx={{ mb: 3 }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio color="success" />}
                    label="Yes - Allow Payout"
                    sx={{ mr: 4, fontWeight: 600 }}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio color="error" />}
                    label="No - Reject Payout"
                    sx={{ fontWeight: 600 }}
                  />
                </RadioGroup>

                {errors.payout && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                    {errors.payout}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  {canWritePayout && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleUpdateStatus}
                      disabled={submitLoading || !isAllowedPayout}
                      startIcon={<Update />}
                      sx={{
                        px: 6,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '15px',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                        borderRadius: 2
                      }}
                    >
                      {submitLoading ? 'Updating...' : 'Update Payout Status'}
                    </Button>
                  )}
                </Box>
              </FormControl>
            </Card>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AllowPayoutCommission;